import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faFileImport,
  faIndustry,
  faFileExcel,
  faPenToSquare,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import CgCnGoodHoverText from 'modules/Common/entities/CgCnGoodHoverText';
import { useSupplierDrafts } from 'modules/FreeSupplier/useSupplierDrafts';
import { useSupplierUploads } from 'modules/FreeSupplier/useSupplierUploads';
import { useSupplierFactorActions } from 'modules/FreeSupplier/useSupplierFactors';
import {
  MANUAL_DRAFT_GROUP_ID,
  draftEmissionsSummary,
  draftReviewStateLabel,
  draftReviewStateTone,
  formatDraftInterval,
  formatUploadDate,
  groupSupplierDraftsByUpload,
  isPendingDraftReviewState,
  planDraftGroupsReveal,
} from 'modules/FreeSupplier/supplierDraftPresentation';
import { formatSupplierPublicEmissions } from 'modules/FreeSupplier/supplierPublicPresentation';
import { SupplierRoutePath, buildSupplierDraftReviewPath } from 'modules/FreeSupplier/routes';
import { useMockData } from '../state/MockDataContext';
import { installationNameById } from '../state/adapters';
import SupplierEmissionsModal from '../components/SupplierEmissionsModal';
import { SupplierCardListSkeleton } from '../components/SupplierLoadingStates';

// One labelled emissions figure in a draft card's compact summary row —
// reuses the same formatter the public supplier page/widget use
// (formatSupplierPublicEmissions) rather than a bespoke number format.
function EmissionStat({ label, value, emphasize }) {
  return (
    <span className="flex flex-col items-end leading-tight">
      <span className="text-[9px] font-medium uppercase tracking-wide text-slate-400">{label}</span>
      <span
        className={`text-xs whitespace-nowrap ${
          emphasize ? 'font-bold text-primaryColor' : 'font-semibold text-slate-600'
        }`}
      >
        {formatSupplierPublicEmissions(value)}
        {emphasize ? <span className="text-[9px] font-normal text-slate-400"> tCO2e/t</span> : null}
      </span>
    </span>
  );
}

function DraftCard({ draft, installationName }) {
  const { t } = useTranslation('freeSupplier');
  const { direct, indirect, total } = draftEmissionsSummary(draft);
  return (
    <Link
      to={buildSupplierDraftReviewPath(draft.id)}
      className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 py-3 no-underline hover:bg-slate-50 transition-colors"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-800 m-0 flex items-center gap-2">
          <FontAwesomeIcon
            icon={faIndustry}
            className="text-slate-300 text-xs"
          />
          {installationName(draft.targetSeriesKey.installationId)}
        </p>
        <p className="text-xs text-slate-500 m-0 mt-0.5 flex flex-wrap items-center gap-x-1">
          {/* Item 16A: the whole card is a <Link> — the hover trigger's own
              click must not bubble into it (a supplier hovering to peek at
              the good shouldn't get navigated away by a stray click). */}
          <span
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <CgCnGoodHoverText
              cnCode={draft.targetSeriesKey.cnCode}
              useAutonomousData
              variant="plain"
            />
          </span>
          <span>
            {draft.targetSeriesKey.productVariant
              ? `· ${draft.targetSeriesKey.productVariant} · `
              : '· '}
            {formatDraftInterval(draft.interval)}
          </span>
        </p>
      </div>
      <div className="shrink-0 flex items-center gap-4">
        <div className="flex items-center gap-3">
          <EmissionStat
            label={t('freeSupplier:draftsInbox.stat.direct', 'Direct')}
            value={direct}
          />
          <EmissionStat
            label={t('freeSupplier:draftsInbox.stat.indirect', 'Indirect')}
            value={indirect}
          />
          <EmissionStat
            label={t('freeSupplier:draftsInbox.stat.total', 'Total')}
            value={total}
            emphasize
          />
        </div>
        <span
          className={`text-[11px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap ${draftReviewStateTone(
            draft.reviewState,
          )}`}
        >
          {draftReviewStateLabel(draft.reviewState)}
        </span>
      </div>
    </Link>
  );
}

// `group.visibleDrafts`/`group.isTruncated` are set by planDraftGroupsReveal
// for the incrementally-revealed pending list (DraftsInboxPage's own "Show
// more" chunking, Item A); the always-render-everything resolved-groups path
// passes a plain group from groupSupplierDraftsByUpload, which has neither —
// falling back to `group.drafts` renders it exactly as before.
function UploadGroup({ group, installationName, onShowAll }) {
  const { t } = useTranslation('freeSupplier');
  // Drafts with no lineage.sourceUploadId at all (manually created, S10) are
  // funnelled by groupSupplierDraftsByUpload into the shared
  // MANUAL_DRAFT_GROUP_ID bucket (also `upload: null`) — render that one
  // distinctly instead of the generic "Uploaded template" fallback used for
  // a real-but-unresolved upload id, so a manual draft never reads as if it
  // came from a file that doesn't exist.
  const isManual = group.uploadId === MANUAL_DRAFT_GROUP_ID;
  const visibleDrafts = group.visibleDrafts || group.drafts;
  return (
    <section className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
      <div className="px-4 py-3 bg-slate-50/60 border-b border-slate-100 flex items-center gap-2">
        <FontAwesomeIcon
          icon={isManual ? faPenToSquare : faFileExcel}
          className={`text-xs shrink-0 ${isManual ? 'text-slate-400' : 'text-emerald-600'}`}
        />
        <p className="text-xs font-semibold text-slate-700 m-0 truncate">
          {isManual
            ? t('freeSupplier:draftsInbox.group.manualDrafts', 'Manual drafts')
            : group.upload?.originalFilename ||
              t('freeSupplier:draftsInbox.group.uploadedTemplateFallback', 'Uploaded template')}
        </p>
        {!isManual && group.upload?.createdAt ? (
          <span className="text-[11px] text-slate-400 ml-auto shrink-0">
            {formatUploadDate(group.upload.createdAt)}
          </span>
        ) : null}
      </div>
      <div className="divide-y divide-slate-100">
        {visibleDrafts.map((draft) => (
          <DraftCard
            key={draft.id}
            draft={draft}
            installationName={installationName}
          />
        ))}
      </div>
      {group.isTruncated ? (
        <div className="px-4 py-2.5 border-t border-slate-100 text-center">
          <button
            type="button"
            onClick={onShowAll}
            className="text-xs font-semibold text-primaryColor bg-transparent border-0 p-0 cursor-pointer hover:underline"
          >
            {t('freeSupplier:draftsInbox.group.showAll', 'Show all {{count}} drafts', {
              count: group.drafts.length,
            })}
          </button>
        </div>
      ) : null}
    </section>
  );
}

// New-build surface (D48): drafts grouped by the source upload that produced
// them (filename + date group header), pending (proposed/under_review) first,
// discarded/published collapsed under a "Resolved" section. A draft with no
// source upload at all (manually created, S10) groups under a neutral
// "Manual drafts" heading instead — see UploadGroup/MANUAL_DRAFT_GROUP_ID.
export default function DraftsInboxPage() {
  const { t } = useTranslation('freeSupplier');
  const { operator, installationDocs } = useMockData();
  const draftsQuery = useSupplierDrafts();
  const uploadsQuery = useSupplierUploads();
  const { invalidate: invalidateFactors } = useSupplierFactorActions();
  const [resolvedOpen, setResolvedOpen] = useState(false);
  // Item 15: "Add emissions" opens the same reused emissions modal
  // EmissionFactorsPage does, but defaults to Draft (D48 wiring — see
  // SupplierEmissionsModal's defaultSaveMode). Target installation follows
  // EmissionFactorsPage's openAdd pattern: the first installationDocs entry
  // (this page has no installation filter to prefer instead).
  const [emissionsModalOpen, setEmissionsModalOpen] = useState(false);
  const hasInstallations = (installationDocs || []).length > 0;
  const openAddEmissions = () => {
    if (!hasInstallations) return;
    setEmissionsModalOpen(true);
  };
  const refreshAfterAddEmissions = async () => {
    await Promise.all([draftsQuery.refetch(), invalidateFactors()]);
  };
  // Item A (owner: "waterfall rendering, no show-more or pages"): client-side
  // incremental reveal for the pending-drafts list — see
  // planDraftGroupsReveal. `revealedChunks` counts "Show more" clicks (starts
  // at 1, i.e. the initial budget only); `expandedGroupIds` are groups the
  // viewer explicitly expanded via their own "Show all N drafts" button.
  const [revealedChunks, setRevealedChunks] = useState(1);
  const [expandedGroupIds, setExpandedGroupIds] = useState(() => new Set());
  const expandGroup = (uploadId) =>
    setExpandedGroupIds((prev) => {
      if (prev.has(uploadId)) return prev;
      const next = new Set(prev);
      next.add(uploadId);
      return next;
    });

  const installationName = (id) => installationNameById(installationDocs, id);

  const drafts = draftsQuery.data?.items || [];
  const uploads = uploadsQuery.data?.items || [];
  const pendingDrafts = useMemo(
    () => drafts.filter((d) => isPendingDraftReviewState(d.reviewState)),
    [drafts],
  );
  const resolvedDrafts = useMemo(
    () => drafts.filter((d) => !isPendingDraftReviewState(d.reviewState)),
    [drafts],
  );
  const pendingGroups = useMemo(
    () => groupSupplierDraftsByUpload(pendingDrafts, uploads),
    [pendingDrafts, uploads],
  );
  const resolvedGroups = useMemo(
    () => groupSupplierDraftsByUpload(resolvedDrafts, uploads),
    [resolvedDrafts, uploads],
  );
  const pendingReveal = useMemo(
    () =>
      planDraftGroupsReveal(pendingGroups, {
        revealedChunks,
        expandedGroupIds: [...expandedGroupIds],
      }),
    [pendingGroups, revealedChunks, expandedGroupIds],
  );

  const isLoading = draftsQuery.isPending || uploadsQuery.isPending;
  const isError = draftsQuery.isError || uploadsQuery.isError;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800 m-0">
            {t('freeSupplier:draftsInbox.title', 'Review & Drafts')}
          </h1>
          <p className="text-sm text-slate-500 mt-1 m-0">
            {t(
              'freeSupplier:draftsInbox.subtitle',
              'Every proposal your uploaded templates produced. Review, correct, and publish — nothing is shared until you confirm.',
            )}
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <Link
            to={SupplierRoutePath.Upload}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-xl px-3 py-1.5 no-underline hover:bg-slate-200 transition-colors"
          >
            <FontAwesomeIcon icon={faFileImport} />
            {t('freeSupplier:draftsInbox.uploadTemplate', 'Upload a template')}
          </Link>
          <CgRadixButton
            variant="primary"
            icon={faPlus}
            onClick={openAddEmissions}
            disabled={!hasInstallations}
            title={
              !hasInstallations
                ? t('freeSupplier:draftsInbox.addInstallationFirst', 'Add an installation first')
                : undefined
            }
          >
            {t('freeSupplier:draftsInbox.addEmissions', 'Add emissions')}
          </CgRadixButton>
        </div>
      </div>

      {isLoading ? (
        // Drafts arrive grouped by the upload that produced them, so the
        // skeleton mirrors those group cards — the list fills in place instead
        // of a centred line of text being replaced by full-height cards.
        <SupplierCardListSkeleton
          cards={2}
          rowsPerCard={3}
        />
      ) : isError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm text-red-700 m-0">
            {t('freeSupplier:draftsInbox.loadError', "We couldn't load your drafts.")}
          </p>
          <CgRadixButton
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => {
              draftsQuery.refetch();
              uploadsQuery.refetch();
            }}
          >
            {t('freeSupplier:draftsInbox.tryAgain', 'Try again')}
          </CgRadixButton>
        </div>
      ) : drafts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center">
          <FontAwesomeIcon
            icon={faFileImport}
            className="text-slate-300 text-2xl mb-3"
          />
          <p className="text-sm text-slate-500 m-0">
            {t('freeSupplier:draftsInbox.emptyState.title', 'No drafts yet.')}
          </p>
          <p className="text-xs text-slate-400 mt-1 mb-4 max-w-sm mx-auto">
            {t(
              'freeSupplier:draftsInbox.emptyState.description',
              'Upload your CBAM communication template, or add values manually — either way they land here as drafts for you to review.',
            )}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Link
              to={SupplierRoutePath.Upload}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-xl px-4 py-2 no-underline hover:bg-slate-200 transition-colors"
            >
              <FontAwesomeIcon icon={faFileImport} />
              {t('freeSupplier:draftsInbox.uploadTemplate', 'Upload a template')}
            </Link>
            <CgRadixButton
              variant="primary"
              icon={faPlus}
              onClick={openAddEmissions}
              disabled={!hasInstallations}
              title={
                !hasInstallations
                  ? t('freeSupplier:draftsInbox.addInstallationFirst', 'Add an installation first')
                  : undefined
              }
            >
              {t('freeSupplier:draftsInbox.addEmissions', 'Add emissions')}
            </CgRadixButton>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {pendingGroups.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">
              {t('freeSupplier:draftsInbox.noPendingDrafts', 'No drafts pending review.')}
            </div>
          ) : (
            pendingReveal.visibleGroups.map((group) => (
              <UploadGroup
                key={group.uploadId}
                group={group}
                installationName={installationName}
                onShowAll={() => expandGroup(group.uploadId)}
              />
            ))
          )}

          {pendingReveal.hasMore ? (
            <div className="flex justify-center">
              <CgRadixButton
                variant="outline"
                size="sm"
                onClick={() => setRevealedChunks((n) => n + 1)}
              >
                {t('freeSupplier:draftsInbox.showMore', 'Show more')}
              </CgRadixButton>
            </div>
          ) : null}

          {resolvedGroups.length > 0 ? (
            <div className="rounded-2xl border border-slate-200 overflow-hidden">
              <button
                type="button"
                onClick={() => setResolvedOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-slate-50/60 border-0 text-left"
              >
                <span className="text-xs font-semibold text-slate-600">
                  {t('freeSupplier:draftsInbox.resolvedCount', 'Resolved ({{count}})', {
                    count: resolvedDrafts.length,
                  })}
                </span>
                <FontAwesomeIcon
                  icon={resolvedOpen ? faChevronDown : faChevronRight}
                  className="text-slate-400 text-xs"
                />
              </button>
              {resolvedOpen ? (
                <div className="flex flex-col gap-4 p-4 bg-white border-t border-slate-100">
                  {resolvedGroups.map((group) => (
                    <UploadGroup
                      key={group.uploadId}
                      group={group}
                      installationName={installationName}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      {/* Item 15: "Add emissions" opens the reused operator emissions modal
          (D48), re-pointed at /supplier/* per SupplierEmissionsModal, with no
          defaultSaveMode override — it opens with Draft preselected. */}
      {emissionsModalOpen && (
        <SupplierEmissionsModal
          show
          onClose={() => setEmissionsModalOpen(false)}
          operator={operator}
          installation={(installationDocs || [])[0]}
          selectedEmission={null}
          handleRefresh={refreshAfterAddEmissions}
        />
      )}
    </div>
  );
}
